import type { FileSystemDirectoryHandle, FileSystemObserver, FileSystemObserverObserveOptions } from "@/types/index"
import JSZip from "jszip"

export async function loadAppZip(filename: string) {
  const response = await fetch(`/apps/${filename}.zip`)

  if (response.status !== 200) {
    throw new Error(`Failed to load app model: ${filename}`)
  }
  const appZipBlob = await response.blob()

  return await loadChunksFromZipFile(appZipBlob)
}

export async function loadChunksFromZipFile(blob: Blob) {
  const loadedZip = await JSZip.loadAsync(blob)

  const fileContents: string[] = []
  const fileNames: string[] = []

  for (const [relativePath, zipEntry] of Object.entries(loadedZip.files)) {
    // Remove hidden __MACOSX files
    if (relativePath.startsWith("__MACOSX")) {
      continue
    }
    if (!zipEntry.dir && (relativePath.toLowerCase().endsWith(".yaml") || relativePath.toLowerCase().endsWith(".yml"))) {
      const fileString = await zipEntry.async("text")
      fileContents.push(fileString)
      fileNames.push(relativePath)
    }
  }

  return {
    fileContents,
    fileNames
  }
}

export async function handleMultipartYamlImport(files: FileList) {
  const fileContents: string[] = []
  const fileNames: string[] = []

  for (const file of Array.from(files)) {
    if ((file.name.toLowerCase().endsWith(".yaml") || file.name.toLowerCase().endsWith(".yml"))) {
      fileContents.push(await file.text())
      fileNames.push(file.name)
    }
  }

  return {
    fileContents,
    fileNames
  }
}

export type ModelChangedCallback = (fileContents: string[], fileNames: string[]) => void
export type ObserverStatechangedCallback = (state: string, payload?: any) => void

export class ModelDirectoryObserver {
  private static readonly OBSERVER_DB_NAME = "ks-observer"
  private static readonly OBSERVER_STORE = "observer"

  public identifier: string
  public modelChanged?: ModelChangedCallback
  public observerStateChanged?: ObserverStatechangedCallback
  public directoryHandle?: FileSystemDirectoryHandle

  public observer?: FileSystemObserver

  private async openDb() {
    const { openDB } = await import("idb")
    return openDB(ModelDirectoryObserver.OBSERVER_DB_NAME, 1, {
      upgrade(db) {
        db.createObjectStore(ModelDirectoryObserver.OBSERVER_STORE)
      }
    })
  }

  private observerKey() {
    return this.identifier
  }

  private observerOptionsKey() {
    return `${this.identifier}-options`
  }

  constructor(identifier: string, modelChanged: ModelChangedCallback, observerStateChanged: ObserverStatechangedCallback) {
    this.identifier = identifier

    this.modelChanged = modelChanged
    this.observerStateChanged = observerStateChanged
  }

  private async saveObserverState(directoryHandle: FileSystemDirectoryHandle, options?: FileSystemObserverObserveOptions) {
    const db = await this.openDb()
    await db.put(ModelDirectoryObserver.OBSERVER_STORE, directoryHandle, this.observerKey())
    if (options) {
      await db.put(ModelDirectoryObserver.OBSERVER_STORE, options, this.observerOptionsKey())
    }

    this.observerStateChanged?.("saved", directoryHandle.name)
  }

  private async loadObserverState(): Promise<{ directoryHandle?: FileSystemDirectoryHandle; options?: FileSystemObserverObserveOptions }> {
    const db = await this.openDb()
    const directoryHandle = await db.get(ModelDirectoryObserver.OBSERVER_STORE, this.observerKey())
    const options = await db.get(ModelDirectoryObserver.OBSERVER_STORE, this.observerOptionsKey())

    return { directoryHandle, options }
  }

  async clearObserverState() {
    const db = await this.openDb()
    await db.delete(ModelDirectoryObserver.OBSERVER_STORE, this.observerKey())
    await db.delete(ModelDirectoryObserver.OBSERVER_STORE, this.observerOptionsKey())

    this.observerStateChanged?.("cleared")
  }

  loadChunksFromDirectory = async (directoryHandle: FileSystemDirectoryHandle) => {
    if (!directoryHandle) {
      throw new Error("No directory handle provided")
    }

    const entries = await directoryYamlEntries(directoryHandle)

    if (!entries) {
      throw new Error("No directory handle")
    }

    this.modelChanged?.(entries.fileContents, entries.fileNames)
  }

  async allEntries(): Promise<{ fileContents: string[]; fileNames: string[] } | undefined> {
    if (!this.directoryHandle) {
      return undefined
    }

    return await directoryYamlEntries(this.directoryHandle)
  }

  // Observe a directory and persist the handle/options
  async createObserver(restoredState?: { directoryHandle?: FileSystemDirectoryHandle; options?: FileSystemObserverObserveOptions }) {
    await this.clearObserverState()

    try {
      const directoryHandle = await (restoredState?.directoryHandle ?? window.showDirectoryPicker()) as FileSystemDirectoryHandle

      if (!directoryHandle) {
        return
      }

      const observerOptions = restoredState?.options ?? { recursive: true }

      this.observer = new window.FileSystemObserver(async () => await this.loadChunksFromDirectory(directoryHandle))

      if (this.observer === undefined) {
        throw new Error("FileSystemObserver is not supported in this environment")
      }

      await this.observer.observe(directoryHandle, observerOptions)

      this.directoryHandle = directoryHandle

      await this.saveObserverState(directoryHandle, observerOptions)

      // Only notify if we created a new observer
      if (!restoredState) {
        this.observerStateChanged?.("created", directoryHandle.name)
      }

      return directoryHandle
    } catch (error) {
      console.error("Error observing directory:", error)
      this.observerStateChanged?.("error", error)
    }
  }

  async restoreObserverState() {
    const restoredState = await this.loadObserverState()
    if (!restoredState.directoryHandle) {
      return
    }

    await this.createObserver(restoredState)

    this.observerStateChanged?.("restored", restoredState.directoryHandle.name)
  }

  async savedModelObserverExists() {
    const { directoryHandle } = await this.loadObserverState()
    return !!directoryHandle
  }

  stopObserver() {
    if (!this.observer) {
      return
    }
    this.observer?.disconnect()
    this.clearObserverState()
    this.observerStateChanged?.("stopped")
  }
}

async function directoryYamlEntries(directoryHandle: FileSystemDirectoryHandle) {
  if (!directoryHandle) {
    return
  }
  const fileContents: string[] = []
  const fileNames: string[] = []

  if (!directoryHandle.entries) {
    throw new Error("Directory handle does not support entries() method")
  }

  for await (const [name, handle] of directoryHandle.entries()) {
    if (
      handle.kind === "file" &&
      (name.toLowerCase().endsWith(".yaml") || name.toLowerCase().endsWith(".yml"))
    ) {
      const file = await handle.getFile()
      fileContents.push(await file.text())
      fileNames.push(name)
    }
  }

  return { fileContents, fileNames }
}
